/* eslint-disable @typescript-eslint/no-unused-vars */
"use client";

import * as React from "react";
import * as SelectPrimitive from "@radix-ui/react-select";
import { Check, ChevronDown, Clock, Globe, Search } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { cn } from "@/lib/utils";
import tzdata from "tzdata";

const hours = Array.from({ length: 24 }, (_, i) =>
  i.toString().padStart(2, "0")
);
const minutes = Array.from({ length: 60 }, (_, i) =>
  i.toString().padStart(2, "0")
);
const timeZones = Object.keys(tzdata.zones);

interface EnhancedTimePickerProps
  extends React.InputHTMLAttributes<HTMLInputElement> {
  onTimeChange?: (time: string, timeZone?: string) => void;
  label?: string;
  showTimeZone?: boolean;
  className?: string;
}

export const EnhancedTimePicker = React.forwardRef<
  HTMLInputElement,
  EnhancedTimePickerProps
>(({ className, onTimeChange, label, showTimeZone = false, ...props }, ref) => {
  const [hour, setHour] = React.useState("00");
  const [minute, setMinute] = React.useState("00");
  const [period, setPeriod] = React.useState<"AM" | "PM">("AM");
  const [timeZone, setTimeZone] = React.useState(() => {
    try {
      return Intl.DateTimeFormat().resolvedOptions().timeZone;
    } catch {
      return "UTC";
    }
  });
  const [searchQuery, setSearchQuery] = React.useState("");

  // Ref to manage focus on the search input
  const searchInputRef = React.useRef<HTMLInputElement>(null);

  // Focus the search input whenever it renders
  React.useEffect(() => {
    if (searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [searchQuery]);

  // Filter time zones based on search query
  const filteredTimeZones = timeZones.filter((tz) =>
    tz.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Handle time change
  const handleTimeChange = (
    newHour: string,
    newMinute: string,
    newPeriod: "AM" | "PM"
  ) => {
    if (onTimeChange) {
      const formattedHour =
        newPeriod === "PM" && newHour !== "12"
          ? (parseInt(newHour) + 12).toString().padStart(2, "0")
          : newPeriod === "AM" && newHour === "12"
          ? "00"
          : newHour;
      const formattedTime = `${formattedHour}:${newMinute}`;
      const formattedTimeZone = showTimeZone ? timeZone : undefined;
      onTimeChange(formattedTime, formattedTimeZone);
    }
  };

  // Reusable Select Component
  const CustomSelect = ({
    value,
    onValueChange,
    placeholder,
    items,
    className,
    ariaLabel,
  }: {
    value: string;
    onValueChange: (value: string) => void;
    placeholder: string;
    items: { value: string; label: string }[];
    className?: string;
    ariaLabel?: string;
  }) => (
    <SelectPrimitive.Root value={value} onValueChange={onValueChange}>
      <SelectPrimitive.Trigger
        aria-label={ariaLabel}
        className={cn(
          "flex h-10 w-[70px] items-center justify-between rounded-md border border-input bg-transparent px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50",
          className
        )}
      >
        <SelectPrimitive.Value placeholder={placeholder} />
        <SelectPrimitive.Icon asChild>
          <ChevronDown className="h-4 w-4 opacity-50" />
        </SelectPrimitive.Icon>
      </SelectPrimitive.Trigger>
      <SelectPrimitive.Portal>
        <SelectPrimitive.Content className="relative z-50 max-h-96 min-w-[8rem] overflow-hidden rounded-md border bg-popover text-popover-foreground shadow-md animate-in fade-in-80">
          <SelectPrimitive.Viewport className="p-1">
            {items.map((item) => (
              <SelectItem key={item.value} value={item.value}>
                {item.label}
              </SelectItem>
            ))}
          </SelectPrimitive.Viewport>
        </SelectPrimitive.Content>
      </SelectPrimitive.Portal>
    </SelectPrimitive.Root>
  );

  // Select Item Component
  const SelectItem = React.forwardRef<
    HTMLDivElement,
    SelectPrimitive.SelectItemProps
  >(({ children, className, ...props }, forwardedRef) => {
    return (
      <SelectPrimitive.Item
        ref={forwardedRef}
        className={cn(
          "relative flex cursor-default select-none items-center rounded-sm py-1.5 pl-8 pr-2 text-sm outline-none focus:bg-accent focus:text-accent-foreground data-[disabled]:pointer-events-none data-[disabled]:opacity-50",
          className
        )}
        {...props}
      >
        <span className="absolute left-2 flex h-3.5 w-3.5 items-center justify-center">
          <SelectPrimitive.ItemIndicator>
            <Check className="h-4 w-4" />
          </SelectPrimitive.ItemIndicator>
        </span>
        <SelectPrimitive.ItemText>{children}</SelectPrimitive.ItemText>
      </SelectPrimitive.Item>
    );
  });

  SelectItem.displayName = "SelectItem";

  return (
    <div className={cn("space-y-2", className)}>
      {label && (
        <label className="text-sm font-medium text-gray-700 dark:text-gray-200">
          {label}
        </label>
      )}
      <motion.div
        className="inline-flex items-center space-x-2 p-2 bg-background dark:bg-card rounded-lg shadow-md"
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
      >
        {/* Hour Select */}
        <CustomSelect
          {...props}
          value={hour}
          onValueChange={(value) => {
            setHour(value);
            handleTimeChange(value, minute, period);
          }}
          placeholder="HH"
          items={hours
            .slice(0, 12)
            .map((h) => ({ value: h, label: h === "00" ? "12" : h }))}
          ariaLabel="Select hour"
        />

        <span className="text-2xl font-light text-gray-400">:</span>

        {/* Minute Select */}
        <CustomSelect
          value={minute}
          onValueChange={(value) => {
            setMinute(value);
            handleTimeChange(hour, value, period);
          }}
          placeholder="MM"
          items={minutes.map((m) => ({ value: m, label: m }))}
          ariaLabel="Select minute"
        />

        {/* Period Select */}
        <CustomSelect
          value={period}
          onValueChange={(value) => {
            if (value === "AM" || value === "PM") {
              setPeriod(value);
              handleTimeChange(hour, minute, value);
            }
          }}
          placeholder="AM/PM"
          items={[
            { value: "AM", label: "AM" },
            { value: "PM", label: "PM" },
          ]}
          ariaLabel="Select period"
        />

        {/* Time Zone Select */}
        {showTimeZone && (
          <SelectPrimitive.Root value={timeZone} onValueChange={setTimeZone}>
            <SelectPrimitive.Trigger
              aria-label="Select time zone"
              className="flex h-10 w-[200px] items-center justify-between rounded-md border border-input bg-transparent px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
            >
              <SelectPrimitive.Value placeholder="Select Time Zone" />
              <SelectPrimitive.Icon asChild>
                <ChevronDown className="h-4 w-4 opacity-50" />
              </SelectPrimitive.Icon>
            </SelectPrimitive.Trigger>
            <SelectPrimitive.Portal>
              <SelectPrimitive.Content className="relative z-50 max-h-96 min-w-[8rem] overflow-hidden rounded-md border bg-popover text-popover-foreground shadow-md animate-in fade-in-80">
                <div className="p-1">
                  <div className="flex items-center border-b px-3 py-2">
                    <Search className="h-4 w-4 text-muted-foreground" />
                    <input
                      ref={searchInputRef}
                      type="text"
                      placeholder="Search time zones..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="ml-2 flex-1 bg-transparent outline-none placeholder:text-muted-foreground"
                    />
                  </div>
                  <SelectPrimitive.Viewport className="p-1">
                    {filteredTimeZones.map((tz) => (
                      <SelectItem key={tz} value={tz}>
                        {tz}
                      </SelectItem>
                    ))}
                  </SelectPrimitive.Viewport>
                </div>
              </SelectPrimitive.Content>
            </SelectPrimitive.Portal>
          </SelectPrimitive.Root>
        )}

        {/* Icon */}
        <AnimatePresence>
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.8 }}
            transition={{ duration: 0.2 }}
          >
            {showTimeZone ? (
              <Globe className="h-5 w-5 text-gray-400" />
            ) : (
              <Clock className="h-5 w-5 text-gray-400" />
            )}
          </motion.div>
        </AnimatePresence>
      </motion.div>
    </div>
  );
});

EnhancedTimePicker.displayName = "EnhancedTimePicker";
